import { z } from 'zod/v3';
import { BugReportStatusSchema, BugReportPrioritySchema } from '../generated/enums';

// ========== Log Entry Schema ==========
export const LogEntrySchema = z.object({
  timestamp: z.number(),
  level: z.enum(['log', 'warn', 'error', 'info', 'debug']),
  message: z.string(),
});

export type LogEntry = z.infer<typeof LogEntrySchema>;

// ========== Environment Info Schema ==========
export const EnvironmentInfoSchema = z.object({
  browser: z.string(),
  browserVersion: z.string(),
  os: z.string(),
  extensionVersion: z.string(),
  screenResolution: z.string().optional(),
  userAgent: z.string().optional(),
});

export type EnvironmentInfo = z.infer<typeof EnvironmentInfoSchema>;

// ========== Step Summary Schema (for recent steps) ==========
export const StepSummarySchema = z.object({
  id: z.string(),
  actionType: z.string(),
  elementInfo: z.string().optional(),
  timestamp: z.number(),
  screenshotUrl: z.string().optional(), // CDN URL after upload
  screenshotDataUrl: z.string().optional(), // Base64 data URL, will be uploaded to R2
  url: z.string().optional(),
});

export type StepSummary = z.infer<typeof StepSummarySchema>;

// ========== Create Bug Report Input Schema ==========
export const CreateBugReportInputSchema = z.object({
  description: z.string().min(10, 'Description must be at least 10 characters'),
  pageUrl: z.string().url().optional(),
  environment: EnvironmentInfoSchema.optional(),
  logs: z.array(LogEntrySchema).optional(),
  domSnapshot: z.string().optional(),
  recentSteps: z.array(StepSummarySchema).optional(),
  screenshotDataUrl: z.string().optional(), // Base64 data URL, will be uploaded to R2
  flowId: z.string().optional(),
});

export type CreateBugReportInput = z.infer<typeof CreateBugReportInputSchema>;

// ========== Bug Report Response Schema ==========
export const BugReportResponseSchema = z.object({
  id: z.string(),
  description: z.string(),
  pageUrl: z.string().nullable(),
  environment: z.any().nullable(),
  screenshotUrl: z.string().nullable(),
  flowId: z.string().nullable(),
  status: BugReportStatusSchema,
  priority: BugReportPrioritySchema,
  createdAt: z.date(),
});

export type BugReportResponse = z.infer<typeof BugReportResponseSchema>;

// ========== Update Bug Report (Admin) Schema ==========
export const UpdateBugReportInputSchema = z.object({
  id: z.string(),
  status: BugReportStatusSchema.optional(),
  priority: BugReportPrioritySchema.optional(),
  internalNotes: z.string().optional(),
  resolution: z.string().optional(),
});

export type UpdateBugReportInput = z.infer<typeof UpdateBugReportInputSchema>;
